import React, { useState, useEffect, useCallback, useRef, JSX } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  useTheme,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import { Add, WorkOutline } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { JobMatchAnalysis, JobMatchScore, JobAnalysisScore } from 'components/JobMatchAnalysis';
import { Candidate, Job } from 'types/types';
import { BackstoryPageProps } from 'components/BackstoryTab';
import { useAuth } from 'hooks/AuthContext';
import { useSelectedCandidate, useSelectedJob } from 'hooks/GlobalContext';
import { CandidatePicker } from 'components/ui/CandidatePicker';
import { JobCreator } from 'components/JobCreator';
import { LoginRestricted } from 'components/ui/LoginRestricted';
import { JobsView } from 'components/ui/JobsView';
import { useNavigate, useParams } from 'react-router-dom';
import { ResumeEdit } from 'components/ui/ResumeEdit';

function WorkAddIcon(): JSX.Element {
  return (
    <Box
      position="relative"
      display="inline-flex"
      sx={{
        lineHeight: '30px',
        mb: '6px',
      }}
    >
      <WorkOutline sx={{ fontSize: 24 }} />
      <Add
        sx={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          fontSize: 14,
          bgcolor: 'background.paper',
          borderRadius: '50%',
          boxShadow: 1,
        }}
        color="primary"
      />
    </Box>
  );
}

interface AnalysisState {
  job: Job | null;
  candidate: Candidate | null;
  analysis: JobAnalysisScore | null;
  resume: string | null;
}

const initialState: AnalysisState = {
  job: null,
  candidate: null,
  analysis: null,
  resume: null,
};

const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Main component
const JobAnalysisPage: React.FC<BackstoryPageProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, guest, apiClient } = useAuth();
  const { candidateId, jobId, stepId } = useParams<{
    candidateId?: string;
    jobId?: string;
    stepId?: string;
  }>();
  const { selectedCandidate, setSelectedCandidate } = useSelectedCandidate();
  const { selectedJob, setSelectedJob } = useSelectedJob();

  const [error, setError] = useState<string | null>(null);
  const [jobTab, setJobTab] = useState<string>('select');
  const [analysisState, setAnalysisState] = useState<AnalysisState>(initialState);
  const [canAdvance, setCanAdvance] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState<number>(user === null ? 0 : 1);
  const maxStep = 4;

  // Initialize from URL params on first load only
  useEffect(() => {
    let isMounted = true;

    const initializeFromParams = async () => {
      try {
        // Initialize candidate
        let candidate = selectedCandidate;
        if (candidateId && candidateId !== selectedCandidate?.id) {
          const fetchedCandidate = await apiClient.getCandidate(candidateId);
          if (fetchedCandidate && isMounted) {
            candidate = fetchedCandidate;
            setSelectedCandidate(fetchedCandidate);
          } else if (isMounted && !fetchedCandidate) {
            setError('Candidate not found.');
          }
        } else if (!candidate && user) {
          // Fallback to user's candidate if no candidate selected
          const userCandidate = await apiClient.getCandidate(user.id || '');
          if (userCandidate && isMounted) {
            candidate = userCandidate;
            setSelectedCandidate(userCandidate);
          }
        }

        // Initialize job
        let job = selectedJob;
        if (jobId && jobId !== selectedJob?.id) {
          const fetchedJob = await apiClient.getJob(jobId);
          if (fetchedJob && isMounted) {
            job = fetchedJob;
            setSelectedJob(fetchedJob);
          } else if (isMounted && !fetchedJob) {
            setError('Job not found.');
          }
        }

        // Initialize step
        let urlStep = stepId ? parseInt(stepId, 10) : undefined;
        // If job and candidate are both provided, default to step 2
        if (urlStep === undefined) {
          urlStep = candidate && job ? 2 : 0;
        }
        if (urlStep !== undefined && !isNaN(urlStep) && urlStep !== activeStep) {
          setActiveStep(urlStep);
        }

        // Set analysis state
        if (isMounted) {
          setAnalysisState({
            ...initialState,
            candidate,
            job,
          });
          setIsInitialized(true);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error during initialization:', err);
          setError('Failed to initialize from URL parameters.');
          setIsInitialized(true);
        }
      }
    };

    initializeFromParams();

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Update URL when state changes (after initialization)
  useEffect(() => {
    if (!isInitialized) return;

    const candidateParam = selectedCandidate?.id || '';
    const jobParam = selectedJob?.id || '';
    const stepParam = activeStep > 0 ? `/${activeStep}` : '';

    let newPath = '/job-analysis';
    if (candidateParam) {
      newPath += `/${candidateParam}`;
      if (jobParam) {
        newPath += `/${jobParam}${stepParam}`;
      }
    }

    // Only navigate if the current path doesn't match
    const currentPath = window.location.pathname;
    if (currentPath !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [selectedCandidate, selectedJob, activeStep, isInitialized, navigate]);

  // Update analysis state when selected candidate/job changes
  useEffect(() => {
    if (!isInitialized) return;

    setAnalysisState(prev => ({
      ...initialState,
      candidate: selectedCandidate,
      job: selectedJob,
      // Preserve analysis if same candidate and job
      analysis:
        prev.candidate === selectedCandidate && prev.job === selectedJob ? prev.analysis : null,
      resume: prev.candidate === selectedCandidate && prev.job === selectedJob ? prev.resume : null,
    }));
  }, [selectedCandidate, selectedJob, isInitialized]);

  const getMissingStepRequirement = useCallback(
    (step: number) => {
      switch (step) {
        case 0: // candidate selection
          break;
        case 1: // job selection
          if (!analysisState.candidate) {
            return 'candidate';
          }
          break;
        case 2: // job analysis
          if (!analysisState.candidate) {
            return 'candidate';
          }
          if (!analysisState.job) {
            return 'job';
          }
          break;
        case 3: // resume generation
          if (!analysisState.candidate) {
            return 'candidate';
          }
          if (!analysisState.job) {
            return 'job';
          }
          if (!analysisState.analysis) {
            return 'analysis';
          }
          break;
      }
      return null;
    },
    [analysisState]
  );

  useEffect(() => {
    if (activeStep === maxStep) {
      setCanAdvance(false);
      return;
    }
    const blocked = getMissingStepRequirement(activeStep + 1);
    setCanAdvance(!blocked);

    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [analysisState, activeStep, getMissingStepRequirement]);

  const handleNext = (): void => {
    if (activeStep === maxStep) {
      return;
    }
    const nextStep = activeStep + 1;
    if (getMissingStepRequirement(nextStep)) {
      return;
    }
    setActiveStep(nextStep);
  };

  const handleBack = (): void => {
    if (activeStep === 0) {
      return;
    }
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const moveToStep = (step: number): void => {
    const missing = getMissingStepRequirement(step);
    if (missing) {
      setError(`${capitalize(missing)} is needed to access this step.`);
      return;
    }
    setActiveStep(step);
  };

  const onCandidateSelect = (candidate: Candidate): void => {
    setSelectedCandidate(candidate);
  };

  const onJobsSelected = (job: Job): void => {
    setSelectedJob(job);
  };

  // Render function for the candidate selection step
  const renderCandidateSelection = (): JSX.Element => (
    <CandidatePicker sx={{ pt: 1 }} onSelect={onCandidateSelect} />
  );

  const handleTabChange = (event: React.SyntheticEvent, value: string): void => {
    setJobTab(value);
  };

  // Render function for the job description step
  const renderJobDescription = (): JSX.Element => {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 0,
          maxHeight: 'min-content',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            m: 0,
          }}
        >
          <Tabs value={jobTab} onChange={handleTabChange} centered>
            <Tab value="select" icon={<WorkOutline />} label="Select Job" />
            <Tab value="create" icon={<WorkAddIcon />} label="Create Job" />
          </Tabs>
        </Box>

        {jobTab === 'select' && (
          <JobsView
            sx={{
              display: 'flex',
              position: 'relative',
              minHeight: 0,
              maxHeight: 'min-content',
              flexGrow: 1,
            }}
            selectable={false}
            onRowClick={onJobsSelected}
            selectedJobId={selectedJob?.id}
            showDetailsPanel={false}
          />
        )}
        {jobTab === 'create' && user && (
          <JobCreator
            onSave={(job): void => {
              onJobsSelected(job);
            }}
          />
        )}
        {jobTab === 'create' && guest && (
          <LoginRestricted>
            <JobCreator
              onSave={(job): void => {
                onJobsSelected(job);
              }}
            />
          </LoginRestricted>
        )}
      </Box>
    );
  };

  const onAnalysisComplete = useCallback(
    (analysis: JobAnalysisScore): void => {
      if (analysis.score === analysisState.analysis?.score) {
        return;
      }
      console.log('Analysis complete:', analysis);
      setAnalysisState(prev => ({ ...prev, analysis }));
    },
    [analysisState.analysis?.score]
  );

  // Render function for the analysis step
  const renderAnalysis = (): JSX.Element => {
    if (!analysisState.job || !analysisState.candidate) {
      return (
        <Box>
          Missing required data:
          {!analysisState.candidate && ' candidate'}
          {!analysisState.job && ' job'}
        </Box>
      );
    }
    return (
      <JobMatchAnalysis
        variant="small"
        job={analysisState.job}
        candidate={analysisState.candidate}
        onAnalysisComplete={onAnalysisComplete}
      />
    );
  };

  const renderResume = (): JSX.Element => {
    return <ResumeEdit />;
  };

  // Don't render until initialized to avoid flash of incorrect state
  if (!isInitialized) {
    return <Box>Loading...</Box>;
  }

  return (
    <Box
      className="JobAnalysisPage"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
      }}
    >
      <Paper
        elevation={4}
        sx={{
          fontSize: isMobile ? '0.875rem' : '1rem',
          display: 'flex',
          position: 'relative',
          m: 0,
          borderRadius: 0,
          mb: 1,
          p: 0,
          gap: 1,
          maxWidth: '100%',
          width: '100%',
          flexDirection: 'column',
          '& .MuiAvatar-root': isMobile
            ? {
                width: 32,
                height: 32,
              }
            : {},
          '& .MuiStepLabel-label': isMobile
            ? {
                fontSize: '0.9rem',
                marginTop: '4px',
              }
            : {},
          '& .MuiSvgIcon-root': isMobile
            ? {
                width: '0.9rem',
                height: '0.9rem',
              }
            : {},
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            mt: 1,
            mb: 1,
            fontWeight: 'bold',
            '& .MuiStepLabel-label': {
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              minWidth: 0,
            },
            '& .MuiStep-root': {
              minWidth: 0,
              px: isMobile ? 0.25 : 1,
            },
            '& .MuiStepLabel-root': {
              minWidth: 0,
            },
            '& .MuiStepLabel-labelContainer': {
              minWidth: 0,
            },
            '& .MuiStepConnector-root': isMobile
              ? {
                  // Shrink connectors so 4 steps fit without clipping on narrow phones
                  marginLeft: '-8px',
                  marginRight: '-8px',
                }
              : {},
            maxWidth: '100%',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Step key={0} sx={{ minWidth: 0 }}>
            <StepLabel
              sx={{ cursor: 'pointer' }}
              onClick={(): void => {
                moveToStep(0);
              }}
              slots={{
                stepIcon: (): JSX.Element => (
                  <Avatar
                    sx={{
                      bgcolor:
                        activeStep >= 0 ? theme.palette.primary.main : theme.palette.grey[300],
                      color: 'white',
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                ),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  '& div': { display: 'flex' },
                  '& :first-of-type': {
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Box sx={{ mb: 1, justifyContent: 'center' }}>
                  Candidate{!isMobile && ' Selection'}
                </Box>
                {selectedCandidate !== null && (
                  <Box
                    sx={{
                      justifySelf: 'flex-start',
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: 'center',
                      fontSize: '0.75rem',
                      gap: isMobile ? 0 : 1,
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      src={
                        selectedCandidate.profileImage
                          ? `/api/1.0/candidates/profile/${selectedCandidate.username}`
                          : ''
                      }
                      alt={`${selectedCandidate.fullName}'s profile`}
                      sx={{
                        alignSelf: isMobile ? 'center' : 'flex-start',
                        width: 32,
                        height: 32,
                        border: '2px solid #e0e0e0',
                      }}
                    />
                    <Box
                      sx={{
                        flexDirection: 'column',
                        textAlign: 'left',
                        minWidth: 0,
                        width: '100%',
                      }}
                    >
                      {!isMobile && <Box>Name</Box>}
                      <Box
                        sx={{
                          fontWeight: 'normal',
                          minWidth: 0,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          justifyContent: isMobile ? 'center' : 'flex-start',
                        }}
                        title={selectedCandidate?.fullName}
                      >
                        {selectedCandidate?.fullName}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </StepLabel>
          </Step>

          <Step key={1} sx={{ minWidth: 0 }}>
            <StepLabel
              sx={{ cursor: 'pointer' }}
              onClick={(): void => {
                moveToStep(1);
              }}
              slots={{
                stepIcon: (): JSX.Element => (
                  <Avatar
                    sx={{
                      bgcolor:
                        activeStep >= 1 ? theme.palette.primary.main : theme.palette.grey[300],
                      color: 'white',
                    }}
                  >
                    <WorkIcon />
                  </Avatar>
                ),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  '& div': { display: 'flex' },
                  '& :first-of-type': {
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Box sx={{ mb: 1, justifyContent: 'center' }}>Job{!isMobile && ' Selection'}</Box>
                {selectedJob !== null && (
                  <Box
                    sx={{
                      justifySelf: 'flex-start',
                      flexDirection: 'column',
                      fontSize: '0.75rem',
                      gap: isMobile ? 0 : 1,
                      width: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ flexDirection: 'column', textAlign: 'left', minWidth: 0 }}>
                      {!isMobile && <Box>Company</Box>}
                      <Box
                        sx={{
                          fontWeight: 'normal',
                          minWidth: 0,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          justifyContent: isMobile ? 'center' : 'flex-start',
                        }}
                        title={selectedJob.company}
                      >
                        {selectedJob.company}
                      </Box>
                    </Box>
                    {!isMobile && (
                      <Box sx={{ flexDirection: 'column', textAlign: 'left', minWidth: 0 }}>
                        <Box>Title</Box>
                        <Box
                          sx={{
                            fontWeight: 'normal',
                            minWidth: 0,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={selectedJob.title}
                        >
                          {selectedJob.title}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </StepLabel>
          </Step>

          <Step key={2} sx={{ minWidth: 0 }}>
            <StepLabel
              sx={{ cursor: 'pointer' }}
              onClick={(): void => {
                moveToStep(2);
              }}
              slots={{
                stepIcon: (): JSX.Element => (
                  <Avatar
                    sx={{
                      bgcolor:
                        activeStep >= 2 ? theme.palette.primary.main : theme.palette.grey[300],
                      color: 'white',
                    }}
                  >
                    <AutoAwesomeIcon />
                  </Avatar>
                ),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  '& div': { display: 'flex' },
                  '& :first-of-type': {
                    whiteSpace: 'nowrap',
                  },
                  m: 0,
                  p: 0,
                }}
              >
                <Box sx={{ mb: 1, justifyContent: 'center' }}>{!isMobile && 'Job '}Analysis</Box>
                {analysisState.analysis !== null && (
                  <JobMatchScore
                    sx={{ width: 'min-content', alignSelf: 'center' }}
                    score={analysisState.analysis.score}
                    variant="small"
                  />
                )}
              </Box>
            </StepLabel>
          </Step>

          <Step key={3} sx={{ minWidth: 0 }}>
            <StepLabel
              sx={{ cursor: 'pointer' }}
              onClick={(): void => {
                moveToStep(3);
              }}
              slots={{
                stepIcon: (): JSX.Element => (
                  <Avatar
                    sx={{
                      bgcolor:
                        activeStep >= 3 ? theme.palette.primary.main : theme.palette.grey[300],
                      color: 'white',
                    }}
                  >
                    <AssessmentIcon />
                  </Avatar>
                ),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  '& div': { display: 'flex' },
                  '& :first-of-type': {
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <Box sx={{ mb: 1, justifyContent: 'center' }}>{!isMobile && 'Edit '}Resume</Box>
              </Box>
            </StepLabel>
          </Step>
        </Stepper>
      </Paper>
      <Box
        ref={scrollRef}
        sx={{
          position: 'relative',
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexGrow: 1,
          overflowY: 'auto',
          m: 0,
          p: 0,
        }}
      >
        {activeStep === 0 && renderCandidateSelection()}
        {activeStep === 1 && renderJobDescription()}
        {activeStep === 2 && renderAnalysis()}
        {activeStep === 3 && renderResume()}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', p: 1 }}>
        <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />

        {activeStep === maxStep ? (
          <Button
            disabled={!canAdvance}
            onClick={(): void => {
              moveToStep(0);
            }}
            variant="outlined"
          >
            Start New Analysis
          </Button>
        ) : (
          <Button disabled={!canAdvance} onClick={handleNext} variant="contained">
            {activeStep === maxStep - 1 ? 'Done' : 'Next'}
          </Button>
        )}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={(): void => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={(): void => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export { JobAnalysisPage };
